import styled from '@emotion/styled';
import { useState } from 'react';
import { api } from '@/api/repository/axios';
import type { SignupHospitalCandidate } from '@/api/repository/types/types';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { DateTimePicker } from '@/components/common/datePicker';
import EmailVerifyField from '@/components/common/organism/EmailVerifyField';
import BusinessCertPhotoField from '@/components/domain/clinic/BusinessCertPhotoField';
import { ErrorMessage, FieldBlock, FORM_COLOR as C, HelpText, Label, StyledInput } from '@/pages/Seminar/components/form/formStyles';
import { businessNumberDigits, formatBusinessNumber, isValidBusinessNumber, isValidHomepage } from '@/utils/clinicFields';
import AddressSearchFiled from '@/components/common/organism/AddressSearchFiled';
import type { KakaoAddressDocument } from '@/common/types';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';

/**
 * 치과 가입 '병원 인증' 단계(약관 다음) — 이메일 인증 → 사업자등록번호·대표자명·개업연월일·병원 이름·치과 주소 → [병원 확인].
 * 같은 이름 병원 중 고른 주소와 맞는 곳으로 확인된다(보통 한 곳 — 드물게 여럿이면 고른다). 다음 단계 칸을 미리 채운다. 안 되면 그 자리에서 다른 방법 —
 * 사업자등록증 사진(주 버튼) 또는 그 아래 보조 선택인 홈페이지 주소 — 중 하나를 해야 다음으로(2026-10-11: 홈페이지는 운영팀 확인 요청).
 * 회원에게는 '확인됐어요'·'확인 중이에요'만 보여 준다(어떻게 확인하는지 말하지 않는다 — 2026-10-10 사용자 결정).
 */

/** 다음 단계(치과 정보)로 넘기는 값 */
export interface HospitalStepResult {
    email: string;
    businessNumber: string;
    ownerName: string;
    /** YYYYMMDD */
    openingDate: string;
    clinicName: string;
    /** 치과 주소(주소 검색으로 고른 것) — 다음 단계 주소 칸에 그대로 */
    address?: string;
    /** 확인된 병원(고른 것) */
    hospital?: SignupHospitalCandidate | null;
    /** 다른 방법 — 홈페이지 */
    homepageUrl?: string | null;
    homepageToken?: string | null;
    homepageAddresses?: string[];
    /** 다른 방법 — 사업자등록증 사진 */
    businessCertToken?: string | null;
}

interface Props {
    initial?: HospitalStepResult | null;
    onBack: () => void;
    onNext: (result: HospitalStepResult) => void;
}

type CheckState = 'idle' | 'confirmed' | 'notConfirmed';
type OtherMethod = 'homepage' | 'cert' | null;

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px 0;
`;

const Lead = styled.p`
    margin: 0;
    padding: 14px 16px;
    border-radius: 12px;
    background: ${C.primaryCard};
    font-size: 14px;
    line-height: 1.6;
    color: ${C.text};
    word-break: keep-all;
`;

const PrimaryButton = styled.button<{ inactive?: boolean }>`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 14px;
    background: ${(p) => (p.inactive ? '#b8cfcb' : C.primary)};
    color: #fff;
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        background: #b8cfcb;
        cursor: default;
    }
`;

const ResultBox = styled.section<{ tone: 'ok' | 'info' }>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${(p) => (p.tone === 'ok' ? C.primary : C.border)};
    background: ${(p) => (p.tone === 'ok' ? C.primarySoft : '#fff')};
`;

const ResultTitle = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${C.text};
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
        display: block;
        width: 22px;
        height: 22px;
        color: ${C.primary};
        flex-shrink: 0;
    }
`;

const ResultText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${C.sub};
    word-break: keep-all;
`;

const CandidateList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Candidate = styled.button<{ selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 56px;
    padding: 10px 14px;
    border-radius: 12px;
    border: ${(p) => (p.selected ? `2px solid ${C.primary}` : `1px solid ${C.border}`)};
    background: #fff;
    text-align: left;
    font-family: inherit;
    cursor: pointer;

    svg {
        display: block;
        width: 22px;
        height: 22px;
        flex-shrink: 0;
        color: ${(p) => (p.selected ? C.primary : C.muted)};
    }

    span {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    b {
        font-size: 15px;
        color: ${C.text};
    }

    small {
        font-size: 13px;
        color: ${C.sub};
        word-break: keep-all;
    }
`;

/** 다른 방법 — 사업자등록증 사진이 주(진한 버튼), 홈페이지 주소는 그 아래 글자 보조(2026-10-11 사용자 결정) */
const MethodPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

/** 보조 선택 — 글자 버튼, 누르는 높이 44px */
const SubMethodButton = styled.button`
    align-self: center;
    min-height: 44px;
    /* 한 줄이면 12 + 20 + 12 = 44px(누르는 높이) */
    padding: 12px 8px;
    border: none;
    background: none;
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-decoration: underline;
    text-underline-offset: 3px;
    word-break: keep-all;
    text-align: center;
    cursor: pointer;
    display: inline-block;

    span {
        white-space: nowrap;
    }
`;

const InlineRow = styled.div`
    display: flex;
    gap: 8px;

    & > input {
        flex: 1;
        min-width: 0;
    }
`;

const SmallButton = styled.button`
    flex-shrink: 0;
    height: 52px;
    padding: 0 16px;
    border-radius: 12px;
    border: 1px solid ${C.primary};
    background: #fff;
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

/** 주소 검색 칸 — 공용 AddressSearchFiled 를 입력칸 모양(52·반경 12·테두리 토큰)으로(치과 정보 수정 화면과 같은 방식) */
const AddressBox = styled.div<{ hasError?: boolean }>`
    > div:first-of-type {
        border: 1px solid ${(p) => (p.hasError ? C.error : C.border)} !important;
        border-radius: 12px !important;
        padding: 0 14px !important;
    }

    > div:first-of-type svg {
        color: ${C.primary};
        flex: 0 0 auto;
    }

    > div:first-of-type p,
    > div:first-of-type span,
    > div:first-of-type div {
        font-size: 15px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
`;

const BackButton = styled.button`
    flex: 0 0 auto;
    height: 52px;
    padding: 0 18px 0 14px;
    border-radius: 14px;
    border: 1px solid ${C.border};
    background: #fff;
    color: ${C.sub};
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    svg {
        display: block;
    }
`;

/** 'YYYY-MM-DD' ↔ 'YYYYMMDD' */
const toDigits = (value: string): string => value.replace(/[^0-9]/g, '').slice(0, 8);
const toDash = (digits: string): string => (digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` : '');
const today = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type FieldKey = 'email' | 'businessNumber' | 'ownerName' | 'openingDate' | 'clinicName' | 'address' | 'check' | 'other';

/** 서버 조회 한도(429) 때 문구 — 서버가 보낸 문구가 없을 때만 */
const HOSPITAL_TOO_MANY_MESSAGE = '잠시 뒤 다시 확인해 주세요. 확인이 계속 안 되면 [사업자등록증 사진으로 확인]을 눌러 주세요.';

const ClinicHospitalVerifyStep = ({ initial, onBack, onNext }: Props) => {
    const [email, setEmail] = useState<string>(initial?.email ?? '');
    const [emailVerified, setEmailVerified] = useState<boolean>(!!initial?.email);
    const [businessNumber, setBusinessNumber] = useState<string>(initial?.businessNumber ?? '');
    const [ownerName, setOwnerName] = useState<string>(initial?.ownerName ?? '');
    const [openingDate, setOpeningDate] = useState<string>(initial?.openingDate ?? '');
    const [clinicName, setClinicName] = useState<string>(initial?.clinicName ?? '');
    const [address, setAddress] = useState<string>(initial?.address ?? '');
    const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
    const [checking, setChecking] = useState<boolean>(false);
    const [state, setState] = useState<CheckState>(initial?.hospital ? 'confirmed' : initial ? 'notConfirmed' : 'idle');
    const [candidates, setCandidates] = useState<SignupHospitalCandidate[]>(initial?.hospital ? [initial.hospital] : []);
    const [picked, setPicked] = useState<SignupHospitalCandidate | null>(initial?.hospital ?? null);
    /** 같은 이름이 너무 많아 목록을 못 보여 줌 — 이름을 정확히 적어 다시 확인하게 */
    const [tooMany, setTooMany] = useState<boolean>(false);
    /** 조회 한도(429) — 안내는 [병원 확인] 아래 오류로, 결과 상자에는 다른 방법만 */
    const [limited, setLimited] = useState<boolean>(false);
    const [method, setMethod] = useState<OtherMethod>(initial?.businessCertToken ? 'cert' : initial?.homepageUrl ? 'homepage' : null);
    const [homepage, setHomepage] = useState<string>(initial?.homepageUrl ?? '');
    const [homepageChecking, setHomepageChecking] = useState<boolean>(false);
    const [homepageResult, setHomepageResult] = useState<{ url: string; token: string | null; addresses: string[] } | null>(
        initial?.homepageUrl ? { url: initial.homepageUrl, token: initial.homepageToken ?? null, addresses: initial.homepageAddresses ?? [] } : null,
    );
    const [certToken, setCertToken] = useState<string | null>(initial?.businessCertToken ?? null);

    const clear = (key: FieldKey) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

    /** 입력을 바꾸면 앞서 받은 결과는 버린다(그 값으로 확인한 결과라서) */
    const resetResult = () => {
        if (state === 'idle') return;
        setState('idle');
        setCandidates([]);
        setPicked(null);
        setTooMany(false);
        setLimited(false);
        clear('check');
        setMethod(null);
        setHomepageResult(null);
        setCertToken(null);
    };

    const validate = (): boolean => {
        const next: Partial<Record<FieldKey, string>> = {};
        if (!emailVerified) next.email = '이메일 인증을 먼저 마쳐 주세요.';
        if (!isValidBusinessNumber(businessNumber)) next.businessNumber = businessNumber ? '사업자등록번호 10자리를 다시 확인해 주세요.' : '사업자등록번호를 입력해 주세요.';
        if (!ownerName.trim()) next.ownerName = '대표자명을 입력해 주세요.';
        if (toDigits(openingDate).length !== 8) next.openingDate = '개업연월일을 골라 주세요.';
        if (!clinicName.trim()) next.clinicName = '병원 이름을 입력해 주세요.';
        if (!address.trim()) next.address = '치과 주소를 검색해 골라 주세요.';
        setErrors(next);
        const first = (Object.keys(next) as FieldKey[])[0];
        if (first) document.getElementById(`hospital-field-${first}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return !first;
    };

    const check = async () => {
        if (!validate()) return;
        setChecking(true);
        clear('check');
        setLimited(false);
        try {
            const res = await api.signup.hospitalCheck({
                email,
                businessNumber: businessNumberDigits(businessNumber),
                ownerName: ownerName.trim(),
                openingDate: toDigits(openingDate),
                clinicName: clinicName.trim(),
                address: address.trim(),
            });
            const result = res.result;
            setTooMany(!!result?.tooMany);
            if (result?.confirmed && result.candidates.length > 0) {
                setCandidates(result.candidates);
                setPicked(result.candidates.length === 1 ? result.candidates[0] : null);
                setState('confirmed');
            } else {
                setCandidates([]);
                setPicked(null);
                setState('notConfirmed');
            }
        } catch (e: any) {
            const message: string | undefined = e?.response?.data?.message;
            if (e?.response?.status === 400 && message) {
                // 서버가 짚은 칸에 — 주소면 주소 칸, 나머지는 사업자등록번호 칸(예전 그대로)
                const field: FieldKey = message.includes('주소') ? 'address' : 'businessNumber';
                setErrors((prev) => ({ ...prev, [field]: message }));
            } else if (e?.response?.status === 429) {
                // 조회 한도 — 칸 아래 오류로 알리고 [사업자등록증 사진으로 확인]은 그대로 누를 수 있게
                setErrors((prev) => ({ ...prev, check: message || HOSPITAL_TOO_MANY_MESSAGE }));
                setLimited(true);
                setTooMany(false);
                setCandidates([]);
                setPicked(null);
                setState('notConfirmed');
            } else if (e?.response?.status === 401) {
                setEmailVerified(false);
                setErrors((prev) => ({ ...prev, email: '이메일 인증을 다시 해 주세요.' }));
            } else {
                // 잠시 확인할 수 없을 때도 막지 않고 다른 방법으로
                setTooMany(false);
                setState('notConfirmed');
            }
        } finally {
            setChecking(false);
        }
    };

    const checkHomepage = async () => {
        if (!isValidHomepage(homepage)) {
            setErrors((prev) => ({ ...prev, other: '치과 홈페이지 주소가 올바르지 않아요(예: www.치과.com)' }));
            return;
        }
        setHomepageChecking(true);
        clear('other');
        try {
            const res = await api.signup.homepageCheck({ email, url: homepage.trim(), businessNumber: businessNumberDigits(businessNumber), ownerName: ownerName.trim() });
            setHomepageResult({ url: homepage.trim(), token: res.result?.confirmed ? res.result.token ?? null : null, addresses: res.result?.addresses ?? [] });
        } catch (e: any) {
            if (e?.response?.status === 400 && e?.response?.data?.message) {
                setErrors((prev) => ({ ...prev, other: e.response.data.message }));
            } else {
                // 오래 걸리거나 오류여도 막지 않는다 — 가입 뒤 확인
                setHomepageResult({ url: homepage.trim(), token: null, addresses: [] });
            }
        } finally {
            setHomepageChecking(false);
        }
    };

    const canNext: boolean =
        (state === 'confirmed' && !!picked) ||
        (state === 'notConfirmed' && ((method === 'homepage' && !!homepageResult) || (method === 'cert' && !!certToken)));

    const next = () => {
        if (!canNext) {
            if (state === 'idle') {
                check();
                return;
            }
            setErrors((prev) => ({
                ...prev,
                other: state === 'confirmed' ? '병원을 골라 주세요.' : '사업자등록증 사진이나 치과 홈페이지 주소로 확인해 주세요.',
            }));
            return;
        }
        onNext({
            email,
            businessNumber: businessNumberDigits(businessNumber),
            ownerName: ownerName.trim(),
            openingDate: toDigits(openingDate),
            clinicName: clinicName.trim(),
            address: address.trim(),
            hospital: state === 'confirmed' ? picked : null,
            homepageUrl: state === 'notConfirmed' && method === 'homepage' ? homepageResult?.url ?? null : null,
            homepageToken: state === 'notConfirmed' && method === 'homepage' ? homepageResult?.token ?? null : null,
            homepageAddresses: state === 'notConfirmed' && method === 'homepage' ? homepageResult?.addresses ?? [] : [],
            businessCertToken: state === 'notConfirmed' && method === 'cert' ? certToken : null,
        });
    };

    return (
        <Wrap>
            <Lead>사업자등록증을 옆에 두고 적어 주세요. 정확히 적으면 가입과 함께 바로 치과 인증이 끝나요.</Lead>

            <FieldBlock id="hospital-field-email">
                <Label htmlFor="hospital-email">로그인 이메일<span>*</span></Label>
                <EmailVerifyField
                    name="email"
                    email={email}
                    onChangeEmail={setEmail}
                    verified={emailVerified}
                    onVerifiedChange={(v) => {
                        setEmailVerified(v);
                        if (v) clear('email');
                    }}
                    hasError={!!errors.email}
                    placeholder="로그인에 쓸 이메일을 입력해 주세요"
                />
                {errors.email && <ErrorMessage role="alert">{errors.email}</ErrorMessage>}
            </FieldBlock>

            <FieldBlock id="hospital-field-businessNumber">
                <Label htmlFor="hospital-bn">사업자등록번호<span>*</span></Label>
                <HelpText>사업자등록증에 적힌 10자리 번호를 그대로 넣어 주세요.</HelpText>
                <StyledInput
                    id="hospital-bn"
                    inputMode="numeric"
                    placeholder="예: 123-45-67890"
                    value={formatBusinessNumber(businessNumber)}
                    hasError={!!errors.businessNumber}
                    onChange={(e) => {
                        setBusinessNumber(businessNumberDigits(e.target.value).slice(0, 10));
                        clear('businessNumber');
                        resetResult();
                    }}
                />
                {errors.businessNumber && <ErrorMessage role="alert">{errors.businessNumber}</ErrorMessage>}
            </FieldBlock>

            <FieldBlock id="hospital-field-ownerName">
                <Label htmlFor="hospital-owner">대표자명<span>*</span></Label>
                <HelpText>사업자등록증의 대표자(성명)와 똑같이 적어 주세요. 공동 대표면 한 분만 적어 주세요.</HelpText>
                <StyledInput
                    id="hospital-owner"
                    placeholder="예: 홍길동"
                    value={ownerName}
                    hasError={!!errors.ownerName}
                    onChange={(e) => {
                        setOwnerName(e.target.value);
                        clear('ownerName');
                        resetResult();
                    }}
                />
                {errors.ownerName && <ErrorMessage role="alert">{errors.ownerName}</ErrorMessage>}
            </FieldBlock>

            <FieldBlock id="hospital-field-openingDate">
                <Label as="p" style={{ marginTop: 0 }}>개업연월일<span>*</span></Label>
                <HelpText>사업자등록증의 개업연월일이에요. 치과 개원일과 다를 수 있어요.</HelpText>
                <DateTimePicker
                    mode="date"
                    title="개업연월일"
                    placeholder="날짜 고르기"
                    value={toDash(openingDate)}
                    yearNav
                    maxDate={today()}
                    showNotes={false}
                    hasError={!!errors.openingDate}
                    onChange={(value) => {
                        setOpeningDate(toDigits(value));
                        clear('openingDate');
                        resetResult();
                    }}
                />
                {errors.openingDate && <ErrorMessage role="alert">{errors.openingDate}</ErrorMessage>}
            </FieldBlock>

            <FieldBlock id="hospital-field-clinicName">
                <Label htmlFor="hospital-name">병원 이름<span>*</span></Label>
                <HelpText>건강보험에 등록된 정식 이름으로 넣어 주세요(예: ○○치과의원). 간판 이름과 다를 수 있어요.</HelpText>
                <StyledInput
                    id="hospital-name"
                    placeholder="예: 웃는치과의원"
                    value={clinicName}
                    hasError={!!errors.clinicName}
                    onChange={(e) => {
                        setClinicName(e.target.value);
                        clear('clinicName');
                        resetResult();
                    }}
                />
                {errors.clinicName && <ErrorMessage role="alert">{errors.clinicName}</ErrorMessage>}
            </FieldBlock>

            <FieldBlock id="hospital-field-address">
                <Label id="hospital-address-label">치과 주소<span>*</span></Label>
                <HelpText>치과 주소를 검색해 골라 주세요.</HelpText>
                <AddressBox hasError={!!errors.address}>
                    <AddressSearchFiled
                        defaultValue={address || undefined}
                        onChange={(doc: KakaoAddressDocument) => {
                            const picked: string = doc?.address_name ?? '';
                            if (!picked || picked === address) return;
                            setAddress(picked);
                            clear('address');
                            resetResult();
                        }}
                        height={52}
                        width={'100%'}
                        border={'none'}
                        margin="0"
                        icon={<PlaceOutlinedIcon aria-hidden />}
                        formPopup
                    />
                </AddressBox>
                {errors.address && <ErrorMessage role="alert">{errors.address}</ErrorMessage>}
            </FieldBlock>

            <PrimaryButton type="button" onClick={check} disabled={checking}>
                {checking ? <ButtonSpinner /> : state === 'idle' ? '병원 확인' : '다시 확인'}
            </PrimaryButton>
            {errors.check && <ErrorMessage role="alert" style={{ marginTop: -16 }}>{errors.check}</ErrorMessage>}

            {state === 'confirmed' && (
                <ResultBox tone="ok" role="status" aria-label="병원 확인 결과">
                    <ResultTitle><CheckCircleRoundedIcon aria-hidden="true" />병원이 확인됐어요</ResultTitle>
                    <ResultText>
                        {candidates.length > 1 ? '같은 주소에 같은 이름의 병원이 여러 곳이에요. 우리 치과를 골라 주세요.' : '다음 단계에 병원 정보를 미리 채워 둘게요. 고칠 수 있어요.'}
                    </ResultText>
                    <CandidateList>
                        {candidates.map((c) => (
                            <Candidate
                                key={c.token}
                                type="button"
                                selected={picked?.token === c.token}
                                aria-pressed={picked?.token === c.token}
                                onClick={() => {
                                    setPicked(c);
                                    clear('other');
                                }}
                            >
                                {picked?.token === c.token ? <CheckCircleRoundedIcon aria-hidden="true" /> : <RadioButtonUncheckedRoundedIcon aria-hidden="true" />}
                                <span>
                                    <b>{c.name}</b>
                                    <small>{[c.address, c.phone].filter(Boolean).join(' · ')}</small>
                                </span>
                            </Candidate>
                        ))}
                    </CandidateList>
                </ResultBox>
            )}

            {state === 'notConfirmed' && (
                <ResultBox tone="info" role="status" aria-label="병원 확인 결과">
                    <ResultTitle>다른 방법으로 확인할게요</ResultTitle>
                    {limited ? null : tooMany ? (
                        <ResultText>검색 결과가 많아요. 병원 이름을 정확히 적어 주세요(예: ○○치과의원).</ResultText>
                    ) : (
                        <ResultText>적은 정보로는 확인하지 못했어요. 사업자등록증과 다시 맞춰 보고 [다시 확인]을 누르거나, 사진으로 확인해 주세요.</ResultText>
                    )}
                    <MethodPanel role="group" aria-label="다른 방법 고르기">
                        {method === 'cert' ? (
                            <BusinessCertPhotoField
                                upload={(file) => api.signup.businessCert(email, file)}
                                onChange={(token) => {
                                    setCertToken(token);
                                    if (token) clear('other');
                                }}
                            />
                        ) : (
                            <PrimaryButton type="button" onClick={() => { setMethod('cert'); clear('other'); }}>
                                사업자등록증 사진으로 확인
                            </PrimaryButton>
                        )}
                        {method !== 'homepage' && (
                            <SubMethodButton type="button" onClick={() => { setMethod('homepage'); clear('other'); }}>
                                {/* 좁은 폭(320)에서는 두 묶음 사이에서만 줄을 바꾼다 */}
                                <span>사업자등록증이 지금 없으면 ·</span> <span>치과 홈페이지 주소로 확인</span>
                            </SubMethodButton>
                        )}
                    </MethodPanel>
                    {method === 'homepage' && (
                        <FieldBlock style={{ marginBottom: 0 }}>
                            <Label htmlFor="hospital-homepage">치과 홈페이지 주소</Label>
                            <InlineRow>
                                <StyledInput
                                    id="hospital-homepage"
                                    inputMode="url"
                                    placeholder="예: www.치과.com"
                                    value={homepage}
                                    onChange={(e) => {
                                        setHomepage(e.target.value);
                                        setHomepageResult(null);
                                        clear('other');
                                    }}
                                />
                                <SmallButton type="button" onClick={checkHomepage} disabled={homepageChecking}>
                                    {homepageChecking ? <ButtonSpinner /> : '확인'}
                                </SmallButton>
                            </InlineRow>
                            {homepageResult && (
                                <ResultText role="status">
                                    확인 요청을 받았어요. 다음 단계로 가 주세요.
                                </ResultText>
                            )}
                        </FieldBlock>
                    )}
                </ResultBox>
            )}
            {errors.other && <ErrorMessage role="alert" style={{ marginTop: -16 }}>{errors.other}</ErrorMessage>}

            <ButtonGroup>
                <BackButton type="button" onClick={onBack}>
                    <KeyboardBackspaceIcon fontSize="small" aria-hidden="true" />이전
                </BackButton>
                {/* 확인 전에는 옅은 색(누르면 무엇이 빠졌는지 알려 준다 — 누를 수 있으니 aria-disabled 는 달지 않는다) — 크기·자리는 그대로 */}
                <PrimaryButton type="button" onClick={next} disabled={checking || homepageChecking} inactive={!canNext} style={{ flex: 1 }}>
                    다음
                </PrimaryButton>
            </ButtonGroup>
        </Wrap>
    );
};

export default ClinicHospitalVerifyStep;
